import React, { useState, useEffect } from 'react';
import { dealsApi, companiesApi, contactsApi, aiApi } from '../lib/api';
import { formatCurrency, formatDate, DEAL_STAGES, PROCUREMENT_SECTORS } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus, DotsSixVertical, CurrencyDollar, Sparkle, X, Lightbulb, Gauge 
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import logger from '../lib/logger';

export default function DealsPage() {
  const [deals, setDeals] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [aiInsight, setAiInsight] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    value: 0,
    stage: 'lead',
    company_id: '',
    contact_id: '',
    expected_close_date: '',
    probability: 0,
    description: '',
    sector: '',
    procurement_type: ''
  });

  useEffect(() => {
    fetchDeals();
    fetchCompanies();
    fetchContacts();
  }, []);

  const fetchDeals = async () => {
    try {
      const response = await dealsApi.getAll({ limit: 200 });
      setDeals(response.data);
    } catch (error) {
      toast.error('Failed to fetch deals');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companiesApi.getAll({ limit: 100 });
      setCompanies(response.data);
    } catch (error) {
      logger.error('Failed to fetch companies:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await contactsApi.getAll({ limit: 100 });
      setContacts(response.data);
    } catch (error) {
      logger.error('Failed to fetch contacts:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDeal) {
        await dealsApi.update(editingDeal.id, formData);
        toast.success('Deal updated');
      } else {
        await dealsApi.create(formData);
        toast.success('Deal created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save deal');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const dealId = result.draggableId;
    const newStage = result.destination.droppableId;
    
    // Optimistic update
    setDeals(prev => prev.map(deal => 
      deal.id === dealId ? { ...deal, stage: newStage } : deal
    ));
    
    try {
      await dealsApi.updateStage(dealId, newStage);
      toast.success('Deal stage updated');
    } catch (error) {
      fetchDeals(); // Revert on error
      toast.error('Failed to update deal stage');
    }
  };

  const openEditDialog = (deal) => {
    setEditingDeal(deal);
    setFormData({
      title: deal.title,
      value: deal.value || 0,
      stage: deal.stage,
      company_id: deal.company_id || '',
      contact_id: deal.contact_id || '',
      expected_close_date: deal.expected_close_date || '',
      probability: deal.probability || 0,
      description: deal.description || '',
      sector: deal.sector || '',
      procurement_type: deal.procurement_type || ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingDeal(null);
    setFormData({
      title: '',
      value: 0,
      stage: 'lead',
      company_id: '',
      contact_id: '',
      expected_close_date: '',
      probability: 0,
      description: '',
      sector: '',
      procurement_type: ''
    });
  };

  const getAiInsight = async (deal) => {
    setAiLoading(true);
    try {
      const response = await aiApi.getInsights({
        deal_id: deal.id,
        prompt_type: 'deal_insights'
      });
      setAiInsight({ deal, ...response.data });
    } catch (error) {
      toast.error('Failed to get AI insights');
    } finally {
      setAiLoading(false);
    }
  };

  const getReadiness = async (deal) => {
    setReadinessLoading(true);
    setReadiness({ deal, loading: true });
    try {
      const response = await dealsApi.readiness(deal.id);
      setReadiness({ deal, ...response.data });
    } catch (error) {
      toast.error('Failed to compute deal readiness');
      setReadiness(null);
    } finally {
      setReadinessLoading(false);
    }
  };

  const getDealsByStage = (stageId) => {
    return deals.filter(deal => deal.stage === stageId);
  };

  const getStageValue = (stageId) => {
    return getDealsByStage(stageId).reduce((sum, deal) => sum + (deal.value || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="deals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Deals Pipeline</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {deals.length} deals • {formatCurrency(deals.reduce((s, d) => s + (d.value || 0), 0))} total value
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsDialogOpen(true); }} 
          className="rounded-sm"
          data-testid="add-deal-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Deal
        </Button>
      </div>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {DEAL_STAGES.map((stage) => (
            <Droppable key={stage.id} droppableId={stage.id}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`flex-shrink-0 w-72 bg-muted/50 rounded-sm ${
                    snapshot.isDraggingOver ? 'ring-2 ring-primary' : ''
                  }`}
                  data-testid={`stage-${stage.id}`}
                >
                  {/* Column Header */}
                  <div className="p-3 border-b border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`h-2 w-2 rounded-full ${stage.color}`}></div>
                        <span className="font-medium text-sm">{stage.label}</span>
                        <Badge variant="secondary" className="text-xs">
                          {getDealsByStage(stage.id).length}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatCurrency(getStageValue(stage.id))}
                    </p>
                  </div>

                  {/* Cards */}
                  <ScrollArea className="h-[calc(100vh-320px)]">
                    <div className="p-2 space-y-2">
                      {getDealsByStage(stage.id).map((deal, index) => (
                        <Draggable key={deal.id} draggableId={deal.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`bg-card border border-border p-3 rounded-sm cursor-pointer hover:-translate-y-[1px] transition-transform ${
                                snapshot.isDragging ? 'shadow-lg ring-2 ring-primary' : ''
                              }`}
                              onClick={() => openEditDialog(deal)}
                              data-testid={`deal-card-${deal.id}`}
                            >
                              <div className="flex items-start gap-2">
                                <div
                                  {...provided.dragHandleProps}
                                  className="mt-1 cursor-grab active:cursor-grabbing"
                                >
                                  <DotsSixVertical size={14} className="text-muted-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-sm truncate">{deal.title}</h4>
                                  {deal.company_name && (
                                    <p className="text-xs text-muted-foreground truncate">
                                      {deal.company_name}
                                    </p>
                                  )}
                                  <div className="flex items-center justify-between mt-2">
                                    <div className="flex items-center gap-1 text-sm font-semibold">
                                      <CurrencyDollar size={14} />
                                      {formatCurrency(deal.value)}
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        getReadiness(deal);
                                      }}
                                      data-testid={`readiness-${deal.id}`}
                                      title="Deal Readiness"
                                    >
                                      <Gauge size={14} className="text-success" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        getAiInsight(deal);
                                      }}
                                      data-testid={`ai-insight-${deal.id}`}
                                    >
                                      <Sparkle size={14} className="text-primary" />
                                    </Button>
                                  </div>
                                  {deal.sector && (
                                    <Badge variant="outline" className="mt-2 text-xs">
                                      {PROCUREMENT_SECTORS.find(s => s.id === deal.sector)?.label || deal.sector}
                                    </Badge>
                                  )}
                                  {deal.expected_close_date && (
                                    <p className="text-xs text-muted-foreground mt-2">
                                      Close: {formatDate(deal.expected_close_date)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {/* AI Insight Panel */}
      {aiInsight && (
        <div className="fixed right-0 top-0 h-full w-96 bg-card border-l border-border shadow-lg z-50 animate-slide-in" data-testid="ai-insight-panel">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lightbulb size={20} className="text-primary" />
              <h3 className="font-semibold">AI Insights</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setAiInsight(null)}>
              <X size={18} />
            </Button>
          </div>
          <div className="p-4 space-y-4">
            <div className="p-3 bg-primary/5 border border-primary/20 rounded-sm">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-1">
                AI INSIGHT
              </p>
              <p className="text-sm">{aiInsight.insight}</p>
            </div>
            {aiInsight.recommendations?.length > 0 && (
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">
                  Recommendations
                </p>
                <ul className="space-y-2">
                  {aiInsight.recommendations.map((rec, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <span className="text-primary">•</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Confidence:</span>
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary" 
                  style={{ width: `${aiInsight.confidence * 100}%` }}
                ></div>
              </div>
              <span>{Math.round(aiInsight.confidence * 100)}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Deal Readiness Panel */}
      {readiness && (
        <div className="fixed right-0 top-0 h-full w-[420px] bg-card border-l border-border shadow-lg z-50 animate-slide-in overflow-y-auto" data-testid="readiness-panel">
          <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-card">
            <div className="flex items-center gap-2">
              <Gauge size={20} className="text-success" />
              <h3 className="font-semibold">Deal Readiness</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setReadiness(null)} data-testid="readiness-close">
              <X size={18} />
            </Button>
          </div>

          {readinessLoading || readiness.loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <Gauge size={32} className="mx-auto mb-3 animate-pulse text-success" />
              Analysing deal signals and generating AI recommendations...
            </div>
          ) : (
            <div className="p-4 space-y-5">
              <div className="text-xs text-muted-foreground truncate" data-testid="readiness-deal-title">
                {readiness.deal_title || readiness.deal?.title}
              </div>

              {/* Score ring */}
              <div className="flex items-center gap-5 p-4 rounded-sm border border-border bg-muted/30">
                <div className="relative h-24 w-24 flex-shrink-0">
                  <svg className="h-24 w-24 -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" stroke="hsl(var(--muted))" strokeWidth="10" fill="none" />
                    <circle
                      cx="50" cy="50" r="42"
                      stroke={readiness.overall_score >= 85 ? '#00B140'
                        : readiness.overall_score >= 70 ? '#41B6E6'
                        : readiness.overall_score >= 50 ? '#FFC72C'
                        : '#E4002B'}
                      strokeWidth="10"
                      fill="none"
                      strokeDasharray={`${(readiness.overall_score / 100) * 263.9} 263.9`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold" data-testid="readiness-score">{readiness.overall_score}</span>
                    <span className="text-[10px] tracking-widest text-muted-foreground">/ 100</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold" data-testid="readiness-grade">{readiness.grade}</span>
                    <Badge
                      variant="outline"
                      className="text-xs"
                      style={{
                        borderColor: readiness.overall_score >= 70 ? '#00B140' : readiness.overall_score >= 50 ? '#FFC72C' : '#E4002B',
                        color: readiness.overall_score >= 70 ? '#00B140' : readiness.overall_score >= 50 ? '#B5940A' : '#E4002B',
                      }}
                    >
                      {readiness.readiness_level}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Weighted across 7 factors</p>
                </div>
              </div>

              {/* AI summary */}
              {readiness.ai_summary && (
                <div className="p-3 bg-primary/5 border border-primary/20 rounded-sm">
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-1">AI SUMMARY</p>
                  <p className="text-sm" data-testid="readiness-summary">{readiness.ai_summary}</p>
                </div>
              )}

              {/* Factors breakdown */}
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Factor breakdown</p>
                <div className="space-y-2" data-testid="readiness-factors">
                  {readiness.factors?.map((f) => (
                    <div key={f.key} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-block h-2 w-2 rounded-full ${
                              f.status === 'good' ? 'bg-success' : f.status === 'warning' ? 'bg-[#FFC72C]' : 'bg-destructive'
                            }`}
                          />
                          <span className="font-medium">{f.label}</span>
                          <span className="text-xs text-muted-foreground">· {f.weight}%</span>
                        </div>
                        <span className="font-mono text-xs">{f.score}/100</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full ${f.status === 'good' ? 'bg-success' : f.status === 'warning' ? 'bg-[#FFC72C]' : 'bg-destructive'}`}
                          style={{ width: `${f.score}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">{f.detail}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommendations */}
              {readiness.recommended_actions?.length > 0 && (
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Next actions</p>
                  <ul className="space-y-2" data-testid="readiness-actions">
                    {readiness.recommended_actions.map((rec, i) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <span className="text-success mt-0.5">✓</span>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDeal ? 'Edit Deal' : 'Add Deal'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Deal Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="deal-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Value</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
                  data-testid="deal-value"
                />
              </div>
              <div className="space-y-2">
                <Label>Stage</Label>
                <Select
                  value={formData.stage}
                  onValueChange={(value) => setFormData({ ...formData, stage: value })}
                >
                  <SelectTrigger data-testid="deal-stage">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEAL_STAGES.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id}>{stage.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company</Label>
                <Select
                  value={formData.company_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, company_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="deal-company">
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Contact</Label>
                <Select
                  value={formData.contact_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, contact_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="deal-contact">
                    <SelectValue placeholder="Select contact" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {contacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.first_name} {contact.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Expected Close Date</Label>
                <Input
                  type="date"
                  value={formData.expected_close_date}
                  onChange={(e) => setFormData({ ...formData, expected_close_date: e.target.value })}
                  data-testid="deal-close-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Probability (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.probability}
                  onChange={(e) => setFormData({ ...formData, probability: parseInt(e.target.value) || 0 })}
                  data-testid="deal-probability"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sector</Label>
                <Select
                  value={formData.sector || "none"}
                  onValueChange={(value) => setFormData({ ...formData, sector: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="deal-sector">
                    <SelectValue placeholder="Select sector" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {PROCUREMENT_SECTORS.map((sector) => (
                      <SelectItem key={sector.id} value={sector.id}>{sector.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Procurement Type</Label>
                <Input
                  value={formData.procurement_type}
                  onChange={(e) => setFormData({ ...formData, procurement_type: e.target.value })}
                  placeholder="e.g., RFP, Tender"
                  data-testid="deal-procurement-type"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="deal-description"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="deal-submit">
                {editingDeal ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
